import React from "react";
import { Tooltip } from "@mui/material";
import { PendingOrder, BulkEditFields } from "../types";
import { calcDefaultPrice, fmtNum, isFieldDirty, formatPrice } from "../utils";
import { isGlobalCountryCode } from "@/utils/country";
import styles from "./ExecutionCell.module.css";

const EXEC_TYPES: [string, string, string][] = [
  ["limit",  "Limit",  "exec-active-limit"],
  ["market", "Market", "exec-active-market"],
];
const METHODS: [string, string, string?][] = [
  ["pct_volume", "x%", "% of Volume"],
];
// "Med" rather than "Medium": the full word is cut to "Medi…" in a three-way toggle
// at this column's width. The button's title carries the full name.
const PRIORITIES: [string, string, string, string][] = [
  ["high",   "High", "exec-active-high",   "High priority"],
  ["medium", "Med",  "exec-active-medium", "Medium priority"],
  ["low",    "Low",  "exec-active-low",    "Low priority"],
];

interface ExecutionCellProps {
  order: PendingOrder;
  isBulkEditing: boolean;
  isPending: boolean;
  draft: Partial<BulkEditFields>;
  /** Field values as they were when edit mode was entered (or, for a create row, its
   *  seeded defaults) — diffed against `draft` to highlight which fields changed. */
  snapshot: Partial<BulkEditFields>;
  firstLatestPrice: number | null | undefined;
  onFieldChange: (field: keyof BulkEditFields, value: BulkEditFields[keyof BulkEditFields]) => void;
  /** When true, the edit controls are greyed out and non-editable
   *  (used on create rows until a Buy/Sell decision is chosen). */
  disabled?: boolean;
}

export const ExecutionCell: React.FC<ExecutionCellProps> = ({
  order,
  isBulkEditing,
  isPending,
  draft,
  snapshot,
  firstLatestPrice,
  onFieldChange,
  disabled = false,
}) => {
  const execType   = draft.execution_type  ?? (isPending ? (order.execution_type  ?? "limit") : "limit");
  const execMethod = draft.execution_method !== undefined
    ? draft.execution_method
    : isPending ? (order.execution_method ?? "pct_volume") : "pct_volume";
  const volPct     = draft.volume_pct      ?? (isPending ? (order.volume_pct != null ? String(order.volume_pct) : "5") : "5");
  const priorityVal = draft.priority       ?? (isPending ? (order.priority ?? "low") : "low");

  // Global (non-Indian) rows price the limit in the trading (LTP) currency: the read
  // display/tooltip use the bracket format ("100.00 (USD)"), the edit input shows the
  // currency code as its prefix ("USD" instead of "₹"). Domestic rows keep "₹".
  const isGlobal = isGlobalCountryCode(order.country_code);
  const limitPriceInputPrefix = isGlobal ? (order.ltp_currency_code ?? "") : "₹";

  // ── Read mode ────────────────────────────────────────────────────────────
  if (!isBulkEditing) {
    if (!isPending) return <td />;

    const priority   = order.priority ?? "low";
    const isMarket   = order.execution_type === "market";
    const hasLimitPrice = order.limit_price != null && order.limit_price !== "" && order.limit_price !== "0";
    const methodText = order.execution_method === "pct_volume"
      ? `${fmtNum(order.volume_pct, 0)}% of volume`
      : null;
    const priorityLabel = priority === "high" ? "High" : priority === "medium" ? "Medium" : "Low";
    const priorityClass = priority === "high" ? "exec-priority-high" : priority === "medium" ? "exec-priority-medium" : "exec-priority-low";

    return (
      <td>
        <Tooltip placement="top" title={
          <span>
            {isMarket ? "Market Order" : "Limit Order"}<br />
            {!isMarket && hasLimitPrice && <>Limit Price: {formatPrice(order.limit_price, order.ltp_currency_code, isGlobal)}<br /></>}
            {methodText && <>Method: {methodText}<br /></>}
            Priority: {priorityLabel}
          </span>
        }>
          <div className={styles["exec-cell"]}>
            <div className={styles["exec-row-1"]}>
              <span className={`${styles["exec-priority"]} ${styles[priorityClass]}`}>
                {priorityLabel}
              </span>
            </div>
            <div>
              {isMarket
                ? <span className={styles["exec-type-market"]}>Market</span>
                : hasLimitPrice
                  ? <span className={styles["exec-price"]}>{formatPrice(order.limit_price, order.ltp_currency_code, isGlobal)}</span>
                  : null}
            </div>
            {methodText && <div><span className={styles["exec-method-text"]}>{methodText}</span></div>}
          </div>
        </Tooltip>
      </td>
    );
  }

  // ── Edit mode ─────────────────────────────────────────────────────────────
  const dirtyExecType   = isFieldDirty(draft, snapshot, "execution_type");
  const dirtyLimitPrice = isFieldDirty(draft, snapshot, "limit_price");
  const dirtyExecMethod = isFieldDirty(draft, snapshot, "execution_method");
  const dirtyVolumePct  = isFieldDirty(draft, snapshot, "volume_pct");
  const dirtyPriority   = isFieldDirty(draft, snapshot, "priority");

  return (
    <td style={{ verticalAlign: "top" }}>
      <div
        className={styles["exec-edit-cell"]}
        style={disabled ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
      >

        {/* Limit | Market */}
        <div className={`${styles["exec-toggle-group"]} ${dirtyExecType ? styles["field-dirty"] : ""}`}>
          {EXEC_TYPES.map(([val, label, activeClass]) => (
            <button key={val} type="button" disabled={disabled}
              className={`${styles["exec-toggle-btn"]} ${execType === val ? `${styles["exec-btn-active"]} ${styles[activeClass]}` : ""}`}
              onClick={() => onFieldChange("execution_type", val)}>
              {label}
            </button>
          ))}
        </div>

        {/* Price — only for Limit */}
        {execType === "limit" && (
          <div className={`${styles["exec-price-input"]} ${dirtyLimitPrice ? styles["field-dirty"] : ""}`}>
            <span className={styles["exec-price-prefix"]}>{limitPriceInputPrefix}</span>
            {/* Rounded on blur, as the CIO % input is: limit_price is a 2-decimal column and
                `step` is advisory only — the browser never rewrites the value, so a typed
                123.456 would otherwise reach the server and fail the whole bulk save.
                Only a field the draft already holds is rounded: an untouched one still
                displays 1.02×LTP, and writing that back on a bare tab-through would dirty
                the row and save a price nobody chose — a moved LTP at that. The save path
                rounds an untouched field itself. */}
            <input type="number" step="0.01" placeholder="Price" disabled={disabled}
              value={draft.limit_price ?? calcDefaultPrice(firstLatestPrice)}
              onChange={e => onFieldChange("limit_price", e.target.value)}
              onBlur={e => {
                if (draft.limit_price === undefined) return;
                const v = parseFloat(e.target.value);
                if (!isNaN(v)) onFieldChange("limit_price", v.toFixed(2));
              }} />
          </div>
        )}

        {/* Method + optional x% */}
        <div className={styles["exec-vol-row"]}>
          <div className={`${styles["exec-toggle-group"]} ${styles["exec-method-group"]} ${dirtyExecMethod ? styles["field-dirty"] : ""}`}>
            {METHODS.map(([val, label, tooltip]) => {
              const isActive = execMethod === val;
              const btn = (
                <button key={val} type="button" disabled={disabled}
                  className={`${styles["exec-toggle-btn"]} ${isActive ? `${styles["exec-btn-active"]} ${styles["exec-active-method"]}` : ""}`}
                  onClick={() => onFieldChange("execution_method", isActive ? null : val as "pct_volume")}>
                  {label}
                </button>
              );
              return tooltip ? <Tooltip key={val} placement="top" title={tooltip}>{btn}</Tooltip> : btn;
            })}
          </div>
          {/* Rounded on blur like the limit price above, and guarded the same way — the
              untouched default here is "5", which rounds to "5.00" and would differ from
              the snapshot, dirtying a row the user never edited. */}
          {execMethod === "pct_volume" && (
            <input type="number" step="0.01" min="1" max="100" disabled={disabled}
              value={volPct} className={`${styles["exec-pct-input"]} ${dirtyVolumePct ? styles["field-dirty"] : ""}`}
              onChange={e => onFieldChange("volume_pct", e.target.value)}
              onBlur={e => {
                if (draft.volume_pct === undefined) return;
                const v = parseFloat(e.target.value);
                if (!isNaN(v)) onFieldChange("volume_pct", v.toFixed(2));
              }} />
          )}
        </div>

        {/* Priority */}
        <div className={`${styles["exec-toggle-group"]} ${dirtyPriority ? styles["field-dirty"] : ""}`}>
          {PRIORITIES.map(([val, label, activeClass, title]) => (
            <button key={val} type="button" disabled={disabled} title={title}
              className={`${styles["exec-toggle-btn"]} ${priorityVal === val ? `${styles["exec-btn-active"]} ${styles[activeClass]}` : ""}`}
              onClick={() => onFieldChange("priority", val)}>
              {label}
            </button>
          ))}
        </div>

      </div>
    </td>
  );
};

ExecutionCell.displayName = "ExecutionCell";
